/**
 * @file   src\components\SearchLocationScreen.js
 * @brief  This file is responsible for search location screen in home page
 * @date   May, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { useEffect, useState } from "react";
import InputGroupControl from "./Common/InputGroup";
import LocationSearchDropdown from "./Common/LocationSearchDropdown";
import { capitalizeString } from "../actions/common";
import { getGeoLocation } from "../services/commonServices";
import {
  preloadPlaces,
  getAutocompletePredictions,
} from "../services/placesService";
import {
  getLocalStorage,
  getUserId,
  getUserType,
  removeLocalStorage,
} from "../helpers/common";
import { Button } from "react-bootstrap";
import HowItWorksTutorial from "./Common/howItWorksTutorial";
import { BUY, USER_TYPE_AGENT } from "../constants/common";
import useAvailableCities from "../hooks/useAvailableCities";
import "../styles/desktop/general.scss";
import FilterIcon from "../images/filter-icon.svg";

const SearchLocationScreen = (props) => {
  const { setUserLocation, checkGitchaAvailableInLoc, adType } = props;
  const { featuredCities, filterCities } = useAvailableCities();
  const [locationList, setLocationList] = useState([]);
  const [availableMarketResults, setAvailableMarketResults] = useState([]);
  const [location, setLocation] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const userLocation = getLocalStorage("userLocation");
  const [formattedAddress, setFormattedAddress] = useState();
  const locationString = getLocalStorage("LocationString");
  const userId = getUserId();
  const userType = getUserType();
  const isAgent = userType === USER_TYPE_AGENT ? true : false;

  // Preload Places script on mount so it's ready when user starts typing
  useEffect(() => {
    preloadPlaces();
  }, []);

  useEffect(() => {
    if (locationString != null && userId) {
      const reqPayload = `address=${capitalizeString(
        locationString,
      )}&components=country:US`;
      getAddresseDetails({ reqPayload: reqPayload });
    }
  }, [locationString, userId]);

  // Show featured cities on focus when input is empty
  const handleFocus = () => {
    setIsFocused(true);
    if (!location || location.trim() === "") {
      setAvailableMarketResults(featuredCities);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
      if (!location || location.trim() === "") {
        setAvailableMarketResults([]);
        setLocationList([]);
      }
    }, 200);
  };

  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    const reqPayload = `address=${value}&components=country:US`;
    if (value.trim() !== "") {
      // Filter available Gitcha cities
      const filtered = filterCities(value.trim());
      setAvailableMarketResults(filtered.slice(0, 3));

      getAddresseDetails({ reqPayload: reqPayload, inputValue: val.trim() });
    } else {
      clearAddressList();
      setAvailableMarketResults(isFocused ? featuredCities : []);
    }
    if (value && value.trim() !== "") {
      setLocation(value);
    } else {
      setLocation("");
      setLocationList([]);
    }
  };

  const clearLocationStates = () => {
    if (!locationString) {
      removeLocalStorage("searchedLocation");

      setFormattedAddress(_.get(userLocation, "formatted_address", null));
      setUserLocation(userLocation);

      setLocation("");
    }
  };

  const getAddresseDetails = async ({ eventType, reqPayload, inputValue }) => {
    const startsWithNumber = inputValue && /^\d/.test(inputValue);
    const isZipCode = inputValue && /^\d{3,5}$/.test(inputValue.trim());

    // locationString auto-select uses geocoding (resolves a known location)
    if (!inputValue && locationString) {
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        const fa = _.get(results[0], "formatted_address", "");
        if (fa !== "United States") {
          handleItemSelection(results[0]);
          removeLocalStorage("LocationString");
        }
      }
      return;
    }

    if (isZipCode) {
      // ZIP code: geocoding is best for exact ZIP resolution
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      const filtered = results.filter((r) => {
        const fa = _.get(r, "formatted_address", "");
        return fa !== "United States";
      });
      setLocationList(filtered.slice(0, 5));
      return;
    }

    // Cities: Places with (cities) type ranks major cities correctly
    // Addresses: Places with address type returns street addresses
    const placesType = startsWithNumber ? ["address"] : ["(cities)"];
    const predictions = await getAutocompletePredictions(
      capitalizeString(inputValue),
      {
        types: placesType,
        componentRestrictions: { country: "us" },
      },
    );

    const items = predictions.map((p) => ({
      _isPlacesPrediction: true,
      place_id: p.place_id,
      formatted_address: p.description,
      types: p.types || [],
    }));

    setLocationList(items.slice(0, 5));
  };

  const clearAddressList = () => {
    setLocationList([]);
    setLocation("");
  };

  // Handle clicking an available market - geocode then use standard selection flow
  const handleAvailableMarketClick = async (market) => {
    const reqPayload = `address=${encodeURIComponent(market.city + ", " + market.state)}&components=country:US`;
    const geoData = await getGeoLocation(reqPayload);
    const results = _.get(geoData, "results", []);
    if (results?.length > 0) {
      handleItemSelection(results[0]);
    }
  };

  // Handle clicking a dropdown item - geocoding result or Places prediction.
  // For Places predictions, geocode the description to get full county info
  // (Places getDetails for cities often omits administrative_area_level_2).
  const handleDropdownClick = async (item) => {
    if (item._isPlacesPrediction) {
      const reqPayload = `address=${encodeURIComponent(item.formatted_address)}&components=country:US`;
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        handleItemSelection(results[0]);
      }
    } else {
      handleItemSelection(item);
    }
  };

  const handleItemSelection = async (address) => {
    let city, state, county;
    removeLocalStorage("searchedLocation");
    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");
    const stateSearched = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }
    const locality = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "locality";
    });
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }
    const countyList = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });

    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }
    if (city != undefined) {
      setFormattedAddress(city + ", " + state);
    } else {
      setFormattedAddress(formatted_address);
    }
    setLocation(city != undefined ? city + ", " + state : formatted_address);
    setAvailableMarketResults([]);
    const locationObject = {
      latitude: _.get(locBounds, "lat", ""),
      longitude: _.get(locBounds, "lng", ""),
      city: city,
      state: state,
      formatted_address:
        city != undefined ? city + ", " + state : formatted_address,
    };
    checkGitchaAvailableInLoc(
      city,
      state,
      county,
      formatted_address,
      locationObject,
    );

    setLocationList([]);
    if (!locationString) {
      removeLocalStorage("searchedLocation");
      setLocation("");
    }
  };

  const showDropdown =
    isFocused && (availableMarketResults.length > 0 || locationList.length > 0);

  return (
    <>
      <div className="agent-home-bg">
        <div className="search_wrap">
          <Button
            className="filter-button filter-icon-btn filter-icon-btn--green"
            onClick={() => props.showFilters()}
          >
            <img src={FilterIcon} alt="Filters" />
          </Button>
          <h2>
            See what {adType === BUY ? "buyers" : "renters"} are looking for
            near you.
          </h2>

          <div>
            <div className="search_field_input  input-group position-relative">
              {location != "" ? (
                <Button
                  variant=""
                  className="search-clear-btn"
                  onClick={() => {
                    clearAddressList();
                    setAvailableMarketResults([]);
                  }}
                ></Button>
              ) : (
                ""
              )}
              <InputGroupControl
                inputGroupClass="form-control pr-0"
                ariaLabel="Example"
                ariaDescribedBy="basic-addon3"
                placeholder="Enter an address, city, or ZIP code"
                errorMessage=""
                value={location ? location : ""}
                iconClass={"search-icon"}
                isIconEnable={true}
                iconId="button-addon3"
                onChange={(e) => {
                  handleLocationChange(e.target.value);
                }}
                noBorder={true}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
              {showDropdown && (
                <LocationSearchDropdown
                  availableMarkets={availableMarketResults}
                  placesResults={locationList}
                  onAvailableMarketClick={handleAvailableMarketClick}
                  onPlacesResultClick={handleDropdownClick}
                  className="mw-100"
                />
              )}
            </div>
          </div>
        </div>
        <HowItWorksTutorial isAgent={isAgent} />
      </div>
    </>
  );
};

export default SearchLocationScreen;
